import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  VStack,
  Text,
  Spinner,
  useToast,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
} from "@chakra-ui/react";
import { DownloadIcon, ViewIcon, DeleteIcon } from "@chakra-ui/icons";
import axiosInstance from "../../helpers/axiosInstance";
import { useHistory } from "react-router-dom";

import Translate from "../LanguageTranslation/Translate";
import Header from "./Header";

const ChatArchive = () => {
  const toast = useToast();
  const history = useHistory();

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const cancelRef = React.useRef();

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await axiosInstance.get("/chat/conversation/user");
        setConversations(response.data.data);
      } catch (err) {
        toast({
          title: "Error",
          description: err.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, [toast]);

  const handleBoxClick = (conversationId) => {
    localStorage.setItem(
      "conversation_id_old",
      localStorage.getItem("conversation_id", conversationId)
    );
    localStorage.setItem("conversation_id", conversationId);
    const conversationType = "general_conversation";
    localStorage.setItem("activeTab", JSON.stringify(conversationType));
    history.push("/ai-travel-planner/chat"); // Use history.push instead of navigate
  };

  const handleDownload = async (conversationId) => {
    try {
      const response = await axiosInstance.get(
        `/chat/conversation/${conversationId}`
      );

      const conversation = response.data.data;
      delete conversation._id;
      delete conversation.viator_api_response;
      delete conversation.home_to_go_api_response;
      delete conversation.outdoorsy_api_response;
      delete conversation.conversation_counter;
      delete conversation.flight_info;

      const url = window.URL.createObjectURL(
        new Blob([JSON.stringify(conversation)], { type: "application/json" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `conversation_${conversation.conversation_name}.json`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (conversationId) => {
    try {
      await axiosInstance.delete(`/chat/conversation/${conversationId}`);
      setConversations((prev) => prev.filter((c) => c._id !== conversationId));
      toast({
        title: "Deleted",
        description: "Conversation deleted successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const openDeleteDialog = (conversationId) => {
    setDeleteId(conversationId);
    setIsAlertOpen(true);
  };

  const closeDeleteDialog = () => {
    setIsAlertOpen(false);
    setDeleteId(null);
  };

  return loading ? (
    <Spinner size="lg" />
  ) : (
    <Box w="full" h="100vh">
      <Header title={<Translate text="Archiving and Dowloading" />} />
      <Box p={4} borderRadius="md" bg="white" w="full">
        <Heading size="md" mb={4}>
          <Translate text="Saved Chats" />
        </Heading>
        <VStack align="start" spacing={4}>
          {conversations.map((c) => (
            <Box
              key={c._id}
              w="full"
              p={4}
              borderRadius="md"
              boxShadow="md"
              bg="gray.50"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              cursor="default"
              _hover={{ bg: "gray.100" }}
            >
              <Text fontWeight="bold" flexGrow={1}>
                {c.conversation_name}
              </Text>
              <Box display="flex" alignItems="center">
                {/* <Button
                  leftIcon={<DownloadIcon />}
                  variant="ghost"
                  size="sm"
                  mr={2}
                >
                  Itinerary
                </Button>
                <Button
                  leftIcon={<DownloadIcon />}
                  variant="ghost"
                  size="sm"
                  mr={2}
                >
                  Budget
                </Button>
                <Button
                  leftIcon={<DownloadIcon />}
                  variant="ghost"
                  size="sm"
                  mr={2}
                >
                  Packing
                </Button> */}
                <IconButton
                  icon={<ViewIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBoxClick(c._id);
                  }}
                  aria-label="View Conversation"
                  variant="ghost"
                />
                <IconButton
                  icon={<DeleteIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteDialog(c._id);
                  }}
                  aria-label="Delete Conversation"
                  variant="ghost"
                  ml={2}
                />
              </Box>
            </Box>
          ))}
        </VStack>
        {loading && (
          <Text mt={2}>
            <Translate text="Updating..." />
          </Text>
        )}
      </Box>
      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeDeleteDialog}
        isCentered
        size={{ base: "xs", md: "md" }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Conversation
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this conversation? This action
              cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeDeleteDialog}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={async () => {
                  await handleDelete(deleteId);
                  closeDeleteDialog();
                }}
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ChatArchive;
